import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  EscalationTrigger,
  HROperationsSummary,
  HRTicket,
  HRTicketAssignmentRule,
  HRTicketAttachment,
  HRTicketCategory,
  HRTicketEscalationRule,
  HRTicketInternalNote,
  HRTicketMessage,
  HRTicketRating,
  HRTicketSLAPolicy,
  HRWorkloadRow,
  TicketPriority,
  TicketStatus,
} from "@/types/domain";

export interface HelpdeskTicketFilters extends PageParams {
  status?: TicketStatus | "";
  priority?: TicketPriority | "";
  category_id?: string;
  employee_id?: string;
  assigned_to?: string;
  // Lets a Manager (helpdesk.view_team) see their own tickets instead of
  // their team's from the same list endpoint - see HelpdeskService.List.
  scope?: "own";
}

export interface CreateTicketInput {
  category_id: string;
  // Only categories with configured sub-categories need one.
  sub_category_id?: string;
  subject: string;
  description: string;
  priority?: TicketPriority;
  attachment?: File;
}

export interface UpdateTicketInput {
  category_id?: string;
  priority?: TicketPriority;
  status?: TicketStatus;
  assigned_to?: string;
  reason?: string;
}

export interface AddMessageInput {
  message: string;
  attachment?: File;
  // cannedResponseId (Phase 4) records that this reply was sent from
  // (possibly edited after) a canned response template - "productivity
  // tracking" for canned-response usage. Never auto-sends anything by
  // itself; the caller still explicitly submits the message.
  cannedResponseId?: string;
}

/** Ticket counts by status, scoped the same way the ticket list is - the
 * "Ticket summary" tile row, not an analytics dashboard. A status absent
 * from the response simply has zero tickets. */
export type TicketSummary = Partial<Record<TicketStatus, number>>;

export const helpdeskApi = {
  categories: (filters: { root_only?: boolean; parent_id?: string; is_active?: boolean } = {}) =>
    http.getPage<HRTicketCategory>("/helpdesk/categories", { per_page: 100, ...filters }),
  createCategory: (input: HelpdeskCategoryInput) => http.post<HRTicketCategory>("/helpdesk/categories", input),
  updateCategory: (id: string, input: Partial<HelpdeskCategoryInput> & { is_active?: boolean; display_order?: number }) =>
    http.patch<HRTicketCategory>(`/helpdesk/categories/${id}`, input),
  // Custom categories only, and only when unreferenced - see
  // HelpdeskService.DeleteCategory. A system category is disabled instead
  // (updateCategory with is_active: false).
  deleteCategory: (id: string) => http.delete(`/helpdesk/categories/${id}`),

  list: (filters: HelpdeskTicketFilters = {}) => http.getPage<HRTicket>("/helpdesk/tickets", filters),
  summary: (filters: HelpdeskTicketFilters = {}) => http.get<TicketSummary>("/helpdesk/tickets/summary", filters),
  get: (id: string) => http.get<HRTicket>(`/helpdesk/tickets/${id}`),

  create: (input: CreateTicketInput) => {
    const form = new FormData();
    form.set("category_id", input.category_id);
    if (input.sub_category_id) form.set("sub_category_id", input.sub_category_id);
    form.set("subject", input.subject);
    form.set("description", input.description);
    if (input.priority) form.set("priority", input.priority);
    if (input.attachment) form.set("attachment", input.attachment);
    return http.post<HRTicket>("/helpdesk/tickets", form);
  },

  update: (id: string, input: UpdateTicketInput) => http.patch<HRTicket>(`/helpdesk/tickets/${id}`, input),

  // Only the ticket's own employee or HR may call this, and only while the
  // ticket is RESOLVED - see HelpdeskService.Reopen.
  reopen: (id: string, reason?: string) => http.post<HRTicket>(`/helpdesk/tickets/${id}/reopen`, { reason }),

  addMessage: (id: string, input: AddMessageInput) => {
    const form = new FormData();
    form.set("message", input.message);
    if (input.attachment) form.set("attachment", input.attachment);
    if (input.cannedResponseId) form.set("canned_response_id", input.cannedResponseId);
    return http.post<HRTicketMessage>(`/helpdesk/tickets/${id}/messages`, form);
  },

  addAttachment: (id: string, file: File) => {
    const form = new FormData();
    form.set("attachment", file);
    return http.post<HRTicketAttachment>(`/helpdesk/tickets/${id}/attachments`, form);
  },

  attachmentUrl: (ticketId: string, attachmentId: string) => `/helpdesk/tickets/${ticketId}/attachments/${attachmentId}`,
  downloadAttachment: (ticketId: string, attachmentId: string) =>
    http.download(`/helpdesk/tickets/${ticketId}/attachments/${attachmentId}`),

  // ---- Phase 3: HR Operations ----

  assign: (id: string, assignedTo: string) => http.post<HRTicket>(`/helpdesk/tickets/${id}/assign`, { assigned_to: assignedTo }),
  reassign: (id: string, assignedTo: string) => http.post<HRTicket>(`/helpdesk/tickets/${id}/reassign`, { assigned_to: assignedTo }),
  addNote: (id: string, note: string) => http.post<HRTicketInternalNote>(`/helpdesk/tickets/${id}/notes`, { note }),

  operations: () => http.get<HROperationsSummary>("/helpdesk/operations"),
  workload: (role?: string) => http.get<HRWorkloadRow[]>("/helpdesk/workload", role ? { role } : undefined),

  slaPolicies: (filters: SLAPolicyFilters = {}) => http.getPage<HRTicketSLAPolicy>("/helpdesk/sla-policies", filters),
  createSLAPolicy: (input: SLAPolicyInput) => http.post<HRTicketSLAPolicy>("/helpdesk/sla-policies", input),
  updateSLAPolicy: (id: string, input: Partial<SLAPolicyInput> & { is_active?: boolean }) =>
    http.patch<HRTicketSLAPolicy>(`/helpdesk/sla-policies/${id}`, input),
  deleteSLAPolicy: (id: string) => http.delete(`/helpdesk/sla-policies/${id}`),

  assignmentRules: (filters: PageParams = {}) => http.getPage<HRTicketAssignmentRule>("/helpdesk/assignment-rules", filters),
  createAssignmentRule: (input: AssignmentRuleInput) => http.post<HRTicketAssignmentRule>("/helpdesk/assignment-rules", input),
  updateAssignmentRule: (id: string, input: Partial<AssignmentRuleInput> & { is_active?: boolean }) =>
    http.patch<HRTicketAssignmentRule>(`/helpdesk/assignment-rules/${id}`, input),
  deleteAssignmentRule: (id: string) => http.delete(`/helpdesk/assignment-rules/${id}`),

  escalationRules: (filters: PageParams = {}) => http.getPage<HRTicketEscalationRule>("/helpdesk/escalation-rules", filters),
  createEscalationRule: (input: EscalationRuleInput) => http.post<HRTicketEscalationRule>("/helpdesk/escalation-rules", input),
  updateEscalationRule: (id: string, input: Partial<EscalationRuleInput> & { is_active?: boolean }) =>
    http.patch<HRTicketEscalationRule>(`/helpdesk/escalation-rules/${id}`, input),
  deleteEscalationRule: (id: string) => http.delete(`/helpdesk/escalation-rules/${id}`),

  // ---- Phase 5: Employee Satisfaction rating ----
  // Only the ticket's own employee may submit; anyone who can view the
  // ticket (owner, or HR/Manager/Payroll Admin per canAccessTicket) may
  // read it back - see HelpdeskService.SubmitRating/GetRating.
  submitRating: (id: string, rating: number, comment?: string) =>
    http.post<HRTicketRating>(`/helpdesk/tickets/${id}/rating`, { rating, comment }),
  getRating: (id: string) => http.get<{ rating: HRTicketRating | null }>(`/helpdesk/tickets/${id}/rating`),
};

export interface HelpdeskCategoryInput {
  code: string;
  name: string;
  description?: string;
  is_active?: boolean;
  parent_id?: string;
  display_order?: number;
}

export interface SLAPolicyFilters extends PageParams {
  category_id?: string;
  priority?: TicketPriority | "";
  is_active?: boolean;
}

export interface SLAPolicyInput {
  category_id?: string;
  priority: TicketPriority;
  first_response_minutes: number;
  resolution_minutes: number;
  business_hours_only?: boolean;
}

export interface AssignmentRuleInput {
  category_id?: string;
  department_id?: string;
  branch_id?: string;
  target_role?: string;
  target_user_id?: string;
  priority_order?: number;
}

export interface EscalationRuleInput {
  level: number;
  trigger_event: EscalationTrigger;
  minutes_after_breach: number;
  notify_role: string;
}
